
import Themeable from 'vuetify/lib/mixins/themeable';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import DNSRepositoryInterface, { DNSRecordsInterface, DNSZonesInterface } from '@/services/DNS/Interfaces';
import DeleteDnsRecordDialog from '@/components/dialogs/dns/DeleteDnsRecordDialog.vue';
import SyncDNSDialog from '@/components/dialogs/dns/SyncDNSDialog.vue';
import ActivateDNSZoneDialog from '@/components/dialogs/dns/ActivateDNSZoneDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'dnsRepository',
  ],
  components: {
    DeleteDnsRecordDialog,
    SyncDNSDialog,
    ActivateDNSZoneDialog,
    PageHeader,
  },
})
export default class ViewDns extends Vue {
  public loading: boolean = true;

  public headers: DataTableHeader[] = [
    { text: 'Host', value: 'content.host' },
    { text: 'Type', value: 'type' },
    { text: 'Priority', value: 'content.priority' },
    { text: 'Value', value: 'displayValue' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public search: string = '';
  public options: ListPaginationOptions = defaultListOptions();
  public readonly LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public readonly dnsRepository!: DNSRepositoryInterface;
  public dnsZone: DNSZonesInterface = {} as DNSZonesInterface;
  public dnsRecords: DNSRecordsInterface[] = [] as DNSRecordsInterface[];

  public deleteDnsRecordDialogData: DNSRecordsInterface | false = false;
  public deleteDnsRecordDialog: string = '';

  public domainForSyncDialog: string = '';
  public syncDNSDialog: string = '';

  public activateDNSDialog: string = '';

  public get dnsRecordsForTable() {
    return this.dnsRecords.map((record) => ({
      ...record,
      displayValue: this.getRecordDisplayValue(record),
    }));
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.options.page = parsed.page;
      this.options.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({
        name: 'dns_zone_view',
        params: { id: this.$route.params.id },
        query: buildListPaginationQuery(this.options),
      });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  @Watch('options.page')
  @Watch('options.itemsPerPage')
  public async onPaginationChange(): Promise<void> {
    await this.syncRouteQuery();
  }

  @Watch('$route')
  public onRouteChange(): void {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'dns_zone_view') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }
    this.applyRouteQuery();
  }

  private getRecordDisplayValue(record: DNSRecordsInterface): string {
    const content = record.content;

    if (record.type === 'SOA') {
      return [
        content.target,
        content.responsible_domain,
        content.serial_number,
        content.refresh_interval,
        content.retry_interval,
        content.expire_interval,
        content.neg_cache_ttl,
      ]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .join(' ');
    }

    if (record.type === 'CAA') {
      return [
        content.flag,
        content.tag,
        content.target ? `${content.target}` : '',
      ]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .join(' ');
    }

    return content?.target || record.target || '';
  }

  public async mounted(): Promise<void> {
    this.applyRouteQuery();
    try {
      this.dnsZone = await this.dnsRepository.getZones(this.$route.params.id);
      await this.refreshRecords();

      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  public createRecord(): void {
    this.$router.push({ name: 'dns_record_create', params: { id: this.dnsZone.id } });
  }

  private async initialize(): Promise<void> {
    this.dnsRecords = await this.dnsRepository.getDNSZoneRecords(this.dnsZone.id);
  }

  public async refreshRecords(): Promise<void> {
    this.loading = true;

    this.dnsRecords = await this.dnsRepository.getDNSZoneRecords(this.dnsZone.id);

    this.loading = false;
  }

  public async deleteRecordPrompt(record: DNSRecordsInterface): Promise<void> {
    this.deleteDnsRecordDialog = 'delete-dns-record-dialog';
    this.deleteDnsRecordDialogData = record;
  }

  public async deleteRecord(record: DNSRecordsInterface): Promise<void> {
    await this.dnsRepository.deleteDNSRecord(this.dnsZone.id, record.id);
    await this.refreshRecords();
    this.deleteDnsRecordDialog = '';
    this.deleteDnsRecordDialogData = false;
  }

  public async updateDNSZoneVisibility(): Promise<void> {
    try {
      this.$successDialog.show('DNS Zone Visibility Updated!');
      this.loading = true;
      await this.dnsRepository.updateDNSZoneVisibility(this.dnsZone.id, this.dnsZone.adminOnly);
      await this.refreshRecords();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  public async showSyncDialog(): Promise<void> {
    this.domainForSyncDialog = this.dnsZone.installation.domain;
    this.syncDNSDialog = 'SyncDNSDialog';
  }

  public async syncDNS(): Promise<void> {
    try {
      await this.dnsRepository.syncDNS(this.domainForSyncDialog, undefined, 0);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }

  public showActivateDialog(): Promise<void> {
    this.activateDNSDialog = 'activate-DNS-zone-dialog';
  }

  public async activateZone(): Promise<void> {
    try {
      await this.dnsRepository.activateDNSZone(this.dnsZone.id);

      this.$successDialog.show('DNS Zone Activation Started!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }
}
